/**
 * useYjsSync React Hook
 *
 * Manages YjsSyncManager lifecycle in React components.
 * Handles connection, disconnection, and message routing between
 * YjsSyncManager and the realtime transport layer.
 *
 * @module features/collab/yjs/hooks/useYjsSync
 * @see SPEC-008 Requirements 2.1, 2.3, 6.1-6.9
 */

import * as Y from 'yjs';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { YjsSyncManager, REMOTE_ORIGIN, type YjsSyncManagerOptions } from '../syncManager';
import type { SyncState, SyncMetrics, YjsUpdateMessage, YjsUpdateResponse } from '@linkode/shared';
import { useAuthStore } from '../../../auth';
import { useRealtime } from '../../transport/realtime-client';

// ============================================================================
// Types
// ============================================================================

/**
 * Options for useYjsSync hook
 */
export interface UseYjsSyncOptions extends YjsSyncManagerOptions {
  /**
   * Whether to automatically connect when the hook mounts
   * @default true
   */
  autoConnect?: boolean;

  /**
   * Initial markdown content to populate Y.Doc if bootstrap fails or returns empty.
   * Used as fallback when server has no Yjs snapshot.
   */
  initialContent?: string;
}

/**
 * Return type for useYjsSync hook
 */
export interface UseYjsSyncResult {
  /** The sync manager instance, or null if not initialized */
  syncManager: YjsSyncManager | null;
  /** Current sync state */
  state: SyncState;
  /** Current sync metrics, or null if not connected */
  metrics: SyncMetrics | null;
  /** Error if sync failed */
  error: Error | null;
  /** Whether the manager is ready (synced) */
  isReady: boolean;
  /** Manually trigger reconnect */
  reconnect: () => Promise<void>;
  /** Y.Doc instance for editor binding */
  yDoc: Y.Doc | null;
  /** Y.Text instance for direct text access */
  yText: Y.Text | null;
}

/**
 * Get API base URL from environment variable
 */
function getApiBaseUrl(): string {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  return '';
}

// ============================================================================
// Publish Yjs Update API
// ============================================================================

/**
 * Publish Yjs update to server via Worker API
 * This broadcasts the update to other connected clients and returns persist lag info.
 *
 * @param docId - Document ID
 * @param message - Yjs update message
 * @param sessionToken - Session token for authentication
 * @returns Response with lagCount for persist lag tracking, or null on error
 * @see SPEC-008 Requirements 9.1, 9.2, 3.9
 */
async function publishYjsUpdate(
  docId: string,
  message: YjsUpdateMessage,
  sessionToken: string | null
): Promise<YjsUpdateResponse | null> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`;
    }
    const response = await fetch(`${getApiBaseUrl()}/realtime/presence`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        channel: `doc:${docId}`,
        event: 'yjs.update',
        data: message,
      }),
    });

    if (!response.ok) {
      console.warn(`[useYjsSync] Yjs update publish failed: ${response.status}`);
      return null;
    }

    // Parse response to get lagCount for persist lag tracking
    const result = (await response.json()) as YjsUpdateResponse;
    return result;
  } catch (err) {
    console.warn('[useYjsSync] Yjs update publish error:', err);
    return null;
  }
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * React hook for managing Yjs synchronization.
 *
 * Creates and manages a YjsSyncManager instance with proper lifecycle handling.
 * Integrates with the realtime transport layer for sending and receiving updates.
 *
 * @param docId - Document ID to sync
 * @param userId - Current user's ID
 * @param options - Configuration options
 * @returns Sync state and manager instance
 *
 * @example
 * ```typescript
 * function Editor({ docId }: { docId: string }) {
 *   const { syncManager, state, error, isReady } = useYjsSync(docId, userId);
 *
 *   if (state === 'bootstrapping') return <div>Loading...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *   if (!isReady) return <div>Connecting...</div>;
 *
 *   return <CollaborativeEditor yDoc={syncManager.getDoc()} />;
 * }
 * ```
 */
export function useYjsSync(
  docId: string,
  userId: string,
  options: UseYjsSyncOptions = {}
): UseYjsSyncResult {
  const { autoConnect = true, initialContent, ...managerOptions } = options;

  // Auth state
  const sessionToken = useAuthStore((state) => state.sessionToken);
  const sessionTokenRef = useRef(sessionToken);
  const initialContentRef = useRef(initialContent);

  // Update refs when values change
  useEffect(() => {
    sessionTokenRef.current = sessionToken;
    // Update manager token so subsequent bootstrap/reconnect uses latest credentials
    managerRef.current?.setSessionToken(sessionToken);
  }, [sessionToken]);

  useEffect(() => {
    initialContentRef.current = initialContent;
  }, [initialContent]);

  // State
  const [state, setState] = useState<SyncState>('disconnected');
  const [error, setError] = useState<Error | null>(null);
  const [metrics, setMetrics] = useState<SyncMetrics | null>(null);
  const [hasAppliedInitialContent, setHasAppliedInitialContent] = useState(false);

  // Refs for stable references
  const managerRef = useRef<YjsSyncManager | null>(null);
  const isMountedRef = useRef(true);
  const isConnectingRef = useRef(false);

  // Create manager instance (memoized)
  const manager = useMemo(() => {
    // Cleanup previous manager if exists with proper disconnect-first pattern
    // SPEC-013 P3: Improved cleanup to avoid race conditions
    if (managerRef.current) {
      const oldManager = managerRef.current;
      const oldDocId = oldManager.getDocId?.() ?? 'unknown';

      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console -- Development-only debugging
        console.log('[useYjsSync] Cleanup: disconnecting old manager', {
          oldDocId,
          newDocId: docId,
          userId,
        });
      }

      // Step 1: Disconnect first to stop any ongoing operations
      oldManager.disconnect();

      // Step 2: Delayed destroy to avoid race conditions with pending callbacks
      setTimeout(() => {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console -- Development-only debugging
          console.log('[useYjsSync] Cleanup: destroying old manager', { oldDocId });
        }
        oldManager.destroy();
      }, 50);
    }

    const newManager = new YjsSyncManager(docId, userId, sessionToken, managerOptions);
    managerRef.current = newManager;
    return newManager;
    // Only recreate manager when docId or userId changes
    // sessionToken and managerOptions are intentionally excluded to avoid unnecessary recreations
  }, [docId, userId]);

  // Get Y.Doc and Y.Text from manager
  const yDoc = useMemo(() => manager?.getDoc() ?? null, [manager]);
  const yText = useMemo(() => yDoc?.getText('content') ?? null, [yDoc]);

  // Apply initial content to Y.Doc if bootstrap returns empty or fails
  // This ensures there's content even when server has no Yjs snapshot
  // SPEC-008: Only apply AFTER bootstrap attempt (synced or error state)
  useEffect(() => {
    if (!yText || !initialContentRef.current || hasAppliedInitialContent) return;

    // Wait until bootstrap has completed (synced) or failed (error)
    // Don't apply during 'disconnected' or 'bootstrapping' states
    if (state !== 'synced' && state !== 'error') return;

    // Only apply if Y.Text is empty (server had no snapshot)
    const currentLength = yText.length;
    if (currentLength === 0) {
      // Apply initial content with REMOTE_ORIGIN to avoid broadcasting
      yDoc?.transact(() => {
        yText.insert(0, initialContentRef.current ?? '');
      }, REMOTE_ORIGIN);
      setHasAppliedInitialContent(true);

      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console -- Development-only debugging
        console.log('[useYjsSync] Applied initial content as fallback');
      }
    }
  }, [yDoc, yText, hasAppliedInitialContent, state]);

  // Send update callback (uses current session token)
  // Updates persist lag from server response
  const sendUpdate = useCallback(
    async (message: YjsUpdateMessage) => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console -- Debug logging
        console.log('[useYjsSync] sendUpdate called', {
          docId,
          hasSessionToken: !!sessionTokenRef.current,
          originalSize: message.originalSize,
        });
      }

      const response = await publishYjsUpdate(docId, message, sessionTokenRef.current);

      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console -- Debug logging
        console.log('[useYjsSync] publishYjsUpdate response', {
          success: !!response,
          lagCount: response?.lagCount,
        });
      }

      // Update persist lag if manager exists and response is valid
      if (response && managerRef.current) {
        managerRef.current.updatePersistLag(response.lagCount);
      }
    },
    [docId]
  );

  // Connect to server
  const connect = useCallback(async () => {
    if (!manager || isConnectingRef.current) return;
    if (manager.getState() !== 'disconnected') return;

    isConnectingRef.current = true;
    setError(null);

    try {
      await manager.connect(sendUpdate);
      if (isMountedRef.current) {
        setState(manager.getState());
        setMetrics(manager.getMetrics());
      }
    } catch (err) {
      if (isMountedRef.current) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        setState('error');
      }
    } finally {
      isConnectingRef.current = false;
    }
  }, [manager, sendUpdate]);

  // Subscribe to manager events
  useEffect(() => {
    if (!manager) return;

    const unsubState = manager.on('stateChange', (newState) => {
      if (isMountedRef.current) {
        setState(newState);
        setMetrics(manager.getMetrics());
      }
    });

    const unsubError = manager.on('error', (err) => {
      if (isMountedRef.current) {
        setError(err);
      }
    });

    // Update metrics periodically when synced
    const unsubUpdateApplied = manager.on('updateApplied', () => {
      if (isMountedRef.current) {
        setMetrics(manager.getMetrics());
      }
    });

    const unsubUpdateSent = manager.on('updateSent', () => {
      if (isMountedRef.current) {
        setMetrics(manager.getMetrics());
      }
    });

    return () => {
      unsubState();
      unsubError();
      unsubUpdateApplied();
      unsubUpdateSent();
    };
  }, [manager]);

  // Handle incoming Yjs updates from realtime
  const handleRealtimeData = useCallback(
    ({ event, data }: { event: string; data: unknown; channel: string }) => {
      if (event === 'yjs.update' && manager && isMountedRef.current) {
        try {
          manager.handleRemoteMessage(data as YjsUpdateMessage);
        } catch (err) {
          console.error('[useYjsSync] Failed to handle remote message:', err);
        }
      }
    },
    [manager]
  );

  // Subscribe to realtime channel for Yjs updates
  const expectedChannel = `doc:${docId}`;
  const shouldSubscribe = state === 'synced' || state === 'syncing';

  useRealtime({
    channels: [expectedChannel],
    events: ['yjs.update'],
    onData: handleRealtimeData,
    enabled: shouldSubscribe,
  });

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect && manager && sessionToken) {
      connect();
    }
  }, [autoConnect, manager, sessionToken, connect]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (managerRef.current) {
        managerRef.current.destroy();
        managerRef.current = null;
      }
    };
  }, []);

  // Reconnect function for manual trigger
  const reconnect = useCallback(async () => {
    if (!manager) return;

    // Disconnect first if connected
    if (manager.getState() !== 'disconnected') {
      manager.disconnect();
    }

    // Wait a tick for state to settle
    await new Promise((resolve) => setTimeout(resolve, 0));

    // Reconnect
    await connect();
  }, [manager, connect]);

  // Computed ready state
  // 說明：同步狀態在本地輸入時會短暫從 synced -> syncing -> synced。
  // 將 syncing 視為 ready，可避免依賴 isReady 的元件（如 EditorShell key）在每次按鍵時被重建。
  const isReady = state === 'synced' || state === 'syncing';

  return {
    syncManager: manager,
    state,
    metrics,
    error,
    isReady,
    reconnect,
    yDoc,
    yText,
  };
}
